/**
 * Operator Users calls (read-only, ids/counts/states only; no user content).
 * Shapes follow backend/src/app/administration/user_inspection.
 */
import { operatorGet, queryString, type OperatorFetch } from "../operatorHttp";

export interface UserListItem {
  user_id: string;
  /** Absent on older backends; null when redacted or not yet captured. */
  telegram_id?: string | null;
  display_name?: string | null;
  status: string;
  locale: string | null;
  timezone: string | null;
  created_at: string | null;
  current_plan_id: string | null;
  requests_30d: number;
  last_activity_at: string | null;
}

export interface UserPage {
  items: UserListItem[];
  next_cursor: string | null;
  /** True when names and Telegram ids are withheld (users.pii.read missing). */
  pii_redacted?: boolean;
}

export interface UserListQuery {
  query?: string;
  status?: string;
  cursor?: string | null;
}

export type JobWindow = "24h" | "7d" | "30d";

export interface JobCounts {
  by_state: Record<string, number>;
  by_request_kind: Record<string, number>;
}

export interface RecentJob {
  job_id: string;
  state: string;
  created_at: string | null;
  correlation_id: string | null;
}

export interface UserActivity {
  jobs: Record<JobWindow, JobCounts>;
  sessions_count: number;
  feedback: { up: number; down: number };
  recent_jobs: RecentJob[];
}

export interface StorageAbility {
  ability_instance_id: string;
  status: string;
  created_at: string | null;
  records_count: number;
  bytes: number;
  last_record_at: string | null;
}

export interface StorageItem {
  kind: string;
  label_key: string;
  count: number;
  bytes: number;
  updated_at: string | null;
}

export interface UserStorage {
  abilities: StorageAbility[];
  totals: {
    abilities_count: number;
    records_count: number;
    bytes_total: number;
  };
  items: StorageItem[];
}

export interface Subscription {
  plan_id: string;
  state: string;
  period_start: string | null;
  period_end: string | null;
}

export interface BillingOrder {
  id: string;
  state: string;
  kind: string;
  amount_stars: number;
  created_at: string | null;
}

export interface BillingRefund {
  id: string;
  order_id: string;
  created_at: string | null;
}

export interface UsagePack {
  id: string;
  tier: string;
  total_credits: number;
  remaining_credits: number;
  issued_at: string | null;
  expires_at: string | null;
}

export interface EntitlementOverride {
  id: string;
  capability: string;
  expires_at: string | null;
  created_at: string | null;
}

export interface UserBilling {
  subscription: Subscription | null;
  entitlement_overrides: EntitlementOverride[];
  usage_this_hour: {
    window: string;
    used_by_operation: Record<string, number>;
  };
  usage_packs: UsagePack[];
  orders: BillingOrder[];
  refunds: BillingRefund[];
  model_cost_30d: { cost_micros: number; calls: number };
}

export interface ExternalIdentity {
  provider: string;
  verification_state: string;
  /** Present only with the users.pii.read permission. */
  provider_subject?: string;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
}

export interface UserDetailData {
  user_id: string;
  profile: {
    display_name?: string | null;
    status: string;
    locale: string | null;
    timezone: string | null;
    created_at: string | null;
  };
  activity: UserActivity;
  workspaces: { count: number; by_status: Record<string, number> };
  abilities_count: number;
  storage: UserStorage;
  billing?: UserBilling;
  billing_redacted?: boolean;
  external_identities: ExternalIdentity[];
  pii_redacted?: boolean;
}

export interface UsersApi {
  list: (query: UserListQuery) => Promise<UserPage>;
  detail: (userId: string) => Promise<UserDetailData>;
}

export const USERS_PAGE_SIZE = 25;
export const USER_STATUSES = ["active", "suspended", "deleted"] as const;

export function createUsersApi(fetcher: OperatorFetch = fetch): UsersApi {
  return {
    list: ({ query, status, cursor }) =>
      operatorGet(
        fetcher,
        `/users${queryString({
          query: query?.trim(),
          status,
          cursor,
          limit: USERS_PAGE_SIZE,
        })}`,
      ),
    detail: (userId) =>
      operatorGet(fetcher, `/users/${encodeURIComponent(userId)}`),
  };
}
